import { useEffect, useState } from 'react'
import { Input, Select, DatePicker } from 'antd'
import debounce from 'lodash/debounce'
import { isEmpty } from 'lodash'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import objectSupport from 'dayjs/plugin/objectSupport'

const { Search } = Input
const { RangePicker } = DatePicker

dayjs.extend(objectSupport)
dayjs.extend(utc)

export const IssueSearch = ({
  issues,
  problemLogsForIssues,
  updateIssuesToShow,
  searchTerm,
  setSearchTerm,
  intl,
}) => {
  const [allIssues, setAllIssues] = useState([])
  useEffect(() => {
    if (!isEmpty(issues) && issues.length > allIssues.length) {
      setAllIssues(issues)
    }
  }, [issues])

  // checks if any of the values in an object contain a term
  const objValsContainTerm = (obj, term) => {
    if (isEmpty(obj)) {
      return false
    }
    return Object.keys(obj).some((k) => {
      return (
        !!obj[k] &&
        typeof obj[k] === 'string' &&
        obj[k].toLowerCase().includes(term.toLowerCase())
      )
    })
  }

  // at least one of these must return true to appear in
  // issues to show
  const matchers = {
    iss: (issue, term) => {
      return objValsContainTerm(issue, term)
    },
    issueID: (issue, term) =>
      issue.id.toLowerCase().includes(term.toLowerCase()),
    problemDescription: (issue, term) => {
      const desc = issue.problem_description
      if (isEmpty(desc)) {
        return false
      }
      return objValsContainTerm(desc, term)
    },
    possibleRootCauses: (issue, term) => {
      const prc = issue.possible_root_causes
      if (isEmpty(prc)) {
        return false
      }
      return objValsContainTerm(prc, term)
    },
    directRootCauses: (issue, term) => {
      const drc = issue.direct_root_causes
      if (isEmpty(drc)) {
        return false
      }
      return (
        objValsContainTerm(drc, term) ||
        drc.five_whys.some((w) => !!w && w.includes(term))
      )
    },
    actions: (issue, term) => {
      const acts = issue.actions
      if (isEmpty(acts)) {
        return false
      }
      return acts.some((act) => {
        return objValsContainTerm(act, term)
      })
    },
    fault: (issue, term) => {
      if (isEmpty(problemLogsForIssues)) {
        return false
      }
      const pls = problemLogsForIssues[issue.id]
      if (isEmpty(pls)) {
        return false
      }
      return pls.some((pl) => objValsContainTerm(pl, term))
    },
  }

  const issueStatusMatches = (issue) => {
    return (
      selectedStatusOption === 'all' || issue.status == selectedStatusOption
    )
  }

  const issueCleanPointMatches = (issue) => {
    if (selectedCleanPointOption === 'yes') {
      return !isEmpty(issue.clean_point)
    } else if (selectedCleanPointOption === 'no') {
      return isEmpty(issue.clean_point)
    }
    return true
  }

  const issueDateRangeMatches = (issue) => {
    if (isEmpty(dateRange)) {
      return true
    }
    const createdAt = dayjs(issue.created_at)
    return createdAt >= dateRange[0] && createdAt <= dateRange[1]
  }

  const issueMatches = (issue, term) => {
    const matchesSearchTerm = !!term
      ? Object.keys(matchers).some((mk) => {
          return matchers[mk](issue, term)
        })
      : true
    const matchesStatus = issueStatusMatches(issue)
    const matchesCleanPoint = issueCleanPointMatches(issue)
    const dateRangeMatches = issueDateRangeMatches(issue)
    const res =
      matchesSearchTerm &&
      matchesStatus &&
      matchesCleanPoint &&
      dateRangeMatches
    return res
  }

  const updateSearchTerm = (evt) => {
    setSearchTerm(evt.target.value)
  }

  const [selectedStatusOption, setSelectedStatusOption] = useState('all')
  const statusOptions = [
    {
      label: 'All',
      value: 'all',
    },
    {
      label: 'Open',
      value: 'open',
    },
    {
      label: 'Closed',
      value: 'closed',
    },
  ]

  const filterOnStatus = (status) => {
    setSelectedStatusOption(status)
  }

  const [selectedCleanPointOption, setSelectedCleanPointOption] = useState(null)
  const cleanPointOptions = [
    {
      label: 'Yes',
      value: 'yes',
    },
    {
      label: 'No',
      value: 'no',
    },
  ]

  const filterOnCleanPoint = (hasCleanPoint) => {
    setSelectedCleanPointOption(hasCleanPoint)
  }

  const todayStart = dayjs().startOf('day')
  const todayEnd = dayjs().endOf('day')
  const [dateRange, setDateRange] = useState(null)
  const filterOnDateRange = (period) => {
    setDateRange(period)
  }

  useEffect(() => {
    if (
      !allIssues ||
      (searchTerm === '' &&
        selectedCleanPointOption === null &&
        selectedStatusOption === 'all' &&
        dateRange === null)
    ) {
      updateIssuesToShow(null)
    } else {
      updateIssuesToShow(
        allIssues.filter((iss) => {
          return issueMatches(iss, searchTerm)
        }),
      )
    }
  }, [
    searchTerm,
    selectedCleanPointOption,
    selectedStatusOption,
    dateRange,
    allIssues,
  ])

  return (
    <>
      <Search
        onChange={updateSearchTerm}
        onSearch={setSearchTerm}
        value={searchTerm}
        placeholder={intl.formatMessage({
          id: 'incidentMgmt.issueSearch.placeholder',
          defaultMessage: 'Search Issues',
        })}
      />
      <div id="issue-search-filters">
        <div class="status-selector">
          <span class="issue-search-filter-label">Issue Status:</span>
          <Select
            key="issue-status-filter"
            defaultValue={selectedStatusOption}
            onChange={(value) => filterOnStatus(value)}
            options={statusOptions}
          />
        </div>
        <div class="clean-point-selector">
          <span class="issue-search-filter-label">Has Clean Point?</span>
          <Select
            key="clean-point-filter"
            onChange={(value) => filterOnCleanPoint(value)}
            options={cleanPointOptions}
          />
        </div>
        <div class="date-range-selector">
          <span class="issue-search-filter-label">Created At:</span>
          <RangePicker
            onChange={(period) => filterOnDateRange(period)}
            presets={[
              {
                label: 'Today',
                value: [todayStart.local(), todayEnd.local()],
              },
            ]}
          />
        </div>
      </div>
    </>
  )
}
